import { useState } from 'react';
import { TriangleAlert as AlertTriangle, Loader as Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  visible: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({ visible, onClose }: Props) {
  const { signOut } = useAuth();
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleClose() {
    setConfirmation('');
    setError('');
    onClose();
  }

  async function handleDelete() {
    if (confirmation !== 'DELETE') return;

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user session');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Account deletion is not available. Contact support.');
      }

      await signOut();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setLoading(false);
    }
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="glass-modal rounded-2xl p-6 w-full max-w-sm">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
            <AlertTriangle size={22} className="text-red-500" />
          </div>
        </div>

        <h2 className="text-base font-bold text-white mb-2 text-center">Delete Account</h2>
        <p className="text-sm text-white text-center mb-1">
          This will permanently delete your account and all associated data.
        </p>
        <p className="text-xs text-red-500 text-center font-medium mb-5">
          This action cannot be undone.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-5">
          <label className="block text-xs font-semibold text-white mb-1.5">
            Type DELETE to confirm
          </label>
          <input
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="DELETE"
            className="w-full px-4 py-2.5 bg-white/10 border border-red-400/30 text-white rounded-xl text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none transition-all duration-200"
          />
        </div>

        <div className="flex gap-2.5">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading || confirmation !== 'DELETE'}
            className="flex-1 px-4 py-2.5 text-sm bg-red-500/10 text-red-600 rounded-xl glass-hover-red font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
